import {
    EacCodeUseTypeEnum,
    EacDataDto,
    EacProvidersEnum,
    validateStrictDecoder,
} from "@flexidao/dto";
import * as D from "schemawax";
import {
    dateDecoder,
    energySourceDecoder,
    siteTypeDecoder,
    uploadWorkflowFileStatusDecoder,
} from "./misc";
import { isoTimestampDecoder } from "../utils";

export const baseRegistryDecoder: D.Decoder<EacDataDto.BaseRegistry> = D.object({
    required: {
        registryId: D.string,
        name: D.string,
        isSupportPendingTransaction: D.boolean,
        countryIds: D.array(D.string),
        eacSchemeId: D.string,
    },
});

export const baseSiteDecoder: D.Decoder<EacDataDto.BaseSite> = D.object({
    required: {
        siteId: D.string,
        tenantId: D.string,
        name: D.string,
        siteType: siteTypeDecoder,
        countryId: D.string,
        biddingZoneId: D.string,
        timezone: D.string,
        syncVersion: dateDecoder,
    },
    optional: {
        eacCodeIds: D.array(D.number),
        energySourceId: energySourceDecoder,
        consumptionContractIds: D.array(D.string),
        productionContractIds: D.array(D.string),
    },
});

export const baseRegistriesArrayDecoder: D.Decoder<Array<EacDataDto.BaseRegistry>> =
    D.array(baseRegistryDecoder);

const countryDecoder: D.Decoder<EacDataDto.Country> = D.object({
    required: {
        countryId: D.string,
        name: D.string,
        enabled: D.boolean,
    },
});

export const countryArrayDecoder: D.Decoder<Array<EacDataDto.Country>> = D.array(countryDecoder);

const eacProviderDecoder: D.Decoder<EacProvidersEnum> = validateStrictDecoder<EacProvidersEnum>()(
    D.literalUnion(...Object.values(EacProvidersEnum)),
);

export const useTypeDecoder: D.Decoder<EacCodeUseTypeEnum> = D.literalUnion(
    EacCodeUseTypeEnum.Beneficiary,
    EacCodeUseTypeEnum.CancellationParty,
    EacCodeUseTypeEnum.ConsumptionSite,
    EacCodeUseTypeEnum.ProductionSite,
);

export const baseEacCodeDecoder: D.Decoder<EacDataDto.BaseEacCode> = D.object({
    required: {
        eacCodeId: D.number,
        eacCode: D.string,
        tenantId: D.string,
        registryId: D.string,
        initialTimeToCheck: dateDecoder,
        enabled: D.boolean,
        useType: useTypeDecoder,
    },
    optional: {
        providerId: eacProviderDecoder,
        credentialId: D.string,
    },
});

export const eacCodeWithRegistryDecoder: D.Decoder<EacDataDto.EacCodeWithRegistry> = D.object({
    required: {
        eacCodeId: D.number,
        eacCode: D.string,
        tenantId: D.string,
        registry: baseRegistryDecoder,
        initialTimeToCheck: dateDecoder,
        enabled: D.boolean,
        useType: useTypeDecoder,
    },
    optional: {
        providerId: eacProviderDecoder,
        credentialId: D.string,
    },
});

export const eacCodeWithRegistryArrayDecoder: D.Decoder<Array<EacDataDto.EacCodeWithRegistry>> =
    D.array(eacCodeWithRegistryDecoder);

const yyyymmddDecoder: D.Decoder<string> = D.string.andThen((str) => str.trim().replace("-", "/"));

const processedCertificateFromPdfDecoder: D.Decoder<EacDataDto.ProcessedCertificateFromPdf> =
    validateStrictDecoder<EacDataDto.ProcessedCertificateFromPdf>()(
        D.object({
            optional: {
                registry: D.nullable(D.string),
                productionCountry: D.nullable(D.string),
                productionState: D.nullable(D.string),
                transactionType: D.nullable(D.string),
                transactionStatus: D.nullable(D.string),
                transactionId: D.nullable(D.string),
                transactionDate: D.nullable(D.string),
                transactionTimezone: D.nullable(D.string),
                fromCertificateId: D.nullable(D.string),
                toCertificateId: D.nullable(D.string),
                issuanceId: D.nullable(D.string),
                quantity: D.nullable(D.oneOf(D.string, D.number)),
                transactionFromName: D.nullable(D.string),
                transactionFromId: D.nullable(D.string),
                transactionToName: D.nullable(D.string),
                transactionToId: D.nullable(D.string),
                productionPeriodStart: D.nullable(yyyymmddDecoder),
                productionPeriodEnd: D.nullable(yyyymmddDecoder),
                productionTimezone: D.nullable(D.string),
                productionSiteName: D.nullable(D.string),
                productionSiteId: D.nullable(D.string),
                productionSiteCommissioningDate: D.nullable(yyyymmddDecoder),
                energySource: D.nullable(D.string),
                consumptionOrganizationName: D.nullable(D.string),
                consumptionOrganizationId: D.nullable(D.string),
                consumptionSiteId: D.nullable(D.string),
                consumptionPeriodStart: D.nullable(yyyymmddDecoder),
                consumptionPeriodEnd: D.nullable(yyyymmddDecoder),
                consumptionTimezone: D.nullable(D.string),
                attachedSourceDocumentName: D.nullable(D.string),
            },
        }),
    );

export const uploadEacPdfResponseDecoder: D.Decoder<EacDataDto.UploadEacPdfResponse> =
    validateStrictDecoder<EacDataDto.UploadEacPdfResponse>()(
        D.object({
            required: {
                certificates: D.array(processedCertificateFromPdfDecoder),
            },
        }),
    );

const supportedEacFormatDecoder: D.Decoder<EacDataDto.SupportedEacFormat> =
    validateStrictDecoder<EacDataDto.SupportedEacFormat>()(
        D.object({
            required: {
                id: D.string,
                displayName: D.string,
            },
            optional: {
                modelVersion: D.nullable(D.string),
                outputVersion: D.nullable(D.string),
            },
        }),
    );

export const supportedEacFormatArrayDecoder: D.Decoder<Array<EacDataDto.SupportedEacFormat>> =
    validateStrictDecoder<Array<EacDataDto.SupportedEacFormat>>()(
        D.array(supportedEacFormatDecoder),
    );

export const uploadWorkflowFileDecoder: D.Decoder<EacDataDto.UploadWorkflowFile> =
    validateStrictDecoder<EacDataDto.UploadWorkflowFile>()(
        D.object({
            required: {
                fileId: D.string,
                fileName: D.string,
                description: D.string,
                uploader: D.string,
                fileParser: D.string,
                uploaded: isoTimestampDecoder,
                lastUpdated: isoTimestampDecoder,
                status: uploadWorkflowFileStatusDecoder,
                details: D.nullable(D.string),
                parsedContent: D.nullable(D.string),
            },
        }),
    );

export const getUploadWorkflowFilesDecoder: D.Decoder<EacDataDto.GetUploadWorkflowFilesResponse> =
    validateStrictDecoder<EacDataDto.GetUploadWorkflowFilesResponse>()(
        D.object({
            required: {
                totalCount: D.number,
                uploadWorkflowFiles: D.array(uploadWorkflowFileDecoder),
            },
        }),
    );

const fileParserDecoder: D.Decoder<EacDataDto.FileParser> =
    validateStrictDecoder<EacDataDto.FileParser>()(
        D.object({
            required: {
                fileParserId: D.string,
                name: D.string,
                description: D.string,
            },
        }),
    );

export const fileParserArrayDecoder: D.Decoder<Array<EacDataDto.FileParser>> =
    validateStrictDecoder<Array<EacDataDto.FileParser>>()(D.array(fileParserDecoder));
